import { X } from '@phosphor-icons/react'
import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

interface ChatModalProps {
  open: boolean
  onClose: () => void
  titleKey: string
  children: ReactNode
  footer?: ReactNode
  role?: 'dialog' | 'alertdialog'
  describedBy?: string
  size?: 'default' | 'tall' | 'members'
  /** Confirmation dialogs are centred on mobile too, not bottom sheets. */
  centered?: boolean
}

/**
 * Accessible chat dialog: centred on desktop, bottom sheet on mobile, portal
 * rendered so the app shell cannot clip it. Chat keeps its own component
 * rather than importing another feature's sheet styles.
 */
export function ChatModal({
  open,
  onClose,
  titleKey,
  children,
  footer,
  role = 'dialog',
  describedBy,
  size = 'default',
  centered = false,
}: ChatModalProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const timer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? panelRef.current)?.focus()
    }, 0)

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [
        ...(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
      ]
      if (focusable.length === 0) {
        event.preventDefault()
        panelRef.current?.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!panelRef.current?.contains(document.activeElement)) {
        event.preventDefault()
        first?.focus()
        return
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
        return
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      restoreFocusRef.current?.focus()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className={`chat-modal__backdrop ${centered ? 'chat-modal__backdrop--centered' : ''}`}
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={`chat-modal ${size === 'tall' ? 'chat-modal--tall' : ''} ${
          size === 'members' ? 'chat-modal--members' : ''
        } ${centered ? 'chat-modal--centered' : ''}`}
      >
        <div className="chat-modal__handle" aria-hidden="true" />
        <div className="chat-modal__header">
          <h2 id={titleId} className="text-2xl">
            {t(titleKey)}
          </h2>
          <button
            type="button"
            className="grid size-11 shrink-0 place-items-center rounded-full text-deep hover:bg-mint"
            onClick={onClose}
          >
            <X size={22} weight="bold" aria-hidden="true" />
            <span className="sr-only">{t('common.close')}</span>
          </button>
        </div>
        <div className="chat-modal__body">{children}</div>
        {footer ? <div className="chat-modal__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
